import { hiringColumnNames, hiringInsuredFields } from "../salud-pymes-helpers";
import { isValidDate } from "../../../../../../utils/time";
import { isValidIban } from "../../../../../../utils/iban";
import {
  getInsuredAge,
  getRelationshipList,
  getValidatedCountryValues,
  getValidatedRelationship,
  getValidCapitalValues,
  validateCoverageAgeRange,
  validateDocument,
  validateEmail,
  validateMobilePhone,
  validatePostalCode,
  validateRequiredCoveragesAge,
} from "./utils";
import { InsuredLegalRepre } from "../../forms/repre-data/repre-data-types";
import { Country, Coverage, HiringJsonData, InsuredData } from "../salud-pymes-insured-types";

export const validateHiringInsureds = async ({
  jsonData,
  maxInsureds,
  coverages,
  countries,
  takerDocNumber,
}: {
  jsonData: HiringJsonData[];
  maxInsureds: number;
  coverages: Coverage[];
  countries: Country[];
  takerDocNumber: string;
}) => {
  const relationshipList = await getRelationshipList();
  const errors: string[] = [];
  const warnings: string[] = [];
  const newInsuredsLength = jsonData && jsonData.length ? jsonData.length : 0;
  const birthDates: string[] = [];
  const docNumbers: Set<string> = new Set();
  const postalCodeList: Set<string> = new Set();
  const emailList: Set<string> = new Set();

  if (newInsuredsLength < 1) {
    errors.push("La hoja de cálculo está vacía");
    return { errors, newInsureds: [] };
  }

  if (newInsuredsLength > maxInsureds) {
    errors.push(`La suma de asegurados es mayor a la permitida de ${maxInsureds} asegurados`);
    return { errors, newInsureds: [] };
  }

  const newInsureds: Partial<InsuredData>[] = [];
  const mandatoryCoverages = coverages.filter(
    (cov: Coverage) => cov.code !== "GS30" && cov.code !== "GS09" && cov.code !== "GS99",
  );
  const coveragesGS30 = coverages.filter((cov: Coverage) => cov.code === "GS30");
  const coveragesGS09 = coverages.filter((cov: Coverage) => cov.code === "GS09");
  const coveragesGS99 = coverages.filter((cov: Coverage) => cov.code === "GS99");
  const GS09ValidValues = getValidCapitalValues(coveragesGS09[0]);
  const GS99ValidValues = getValidCapitalValues(coveragesGS99[0]);

  for (let index = 0; index < jsonData.length; index++) {
    const newInsured = jsonData[index];
    const rowNumber = index + 2; // Sumamos 2 porque el index es 0 y la primera línea el nombre de las columnas

    // Validación nombre de columnas
    if (index === 0) {
      let columnErrors = false;
      if (Object.keys(hiringColumnNames).length !== Object.keys(newInsured).length) {
        errors.push(`El número de columnas de la hoja de cálculo no coincide con el requerido.`);
        return { errors, newInsureds };
      }

      for (const key of Object.keys(newInsured)) {
        if (!(key in hiringColumnNames)) {
          errors.push(`La columna ${key} no coincide con las columnas requeridas.`);
          columnErrors = true;
        }
      }

      if (columnErrors) {
        return { errors, newInsureds };
      }
    }

    // Validación FECHA DE NACIMIENTO
    const birthDate = newInsured[hiringInsuredFields.birthDate];
    if (!birthDate) {
      errors.push(`La fecha de nacimiento es obligatoria (fila ${rowNumber})`);
    } else {
      const birthDateErrors = validateRequiredCoveragesAge(birthDate, coverages, rowNumber);
      if (birthDateErrors.length) {
        errors.push(...birthDateErrors);
      }
    }

    // Validación SEXO
    const gender = newInsured[hiringInsuredFields.gender];
    if (gender !== "M" && gender !== "H") {
      errors.push(
        `El género no puede estar vacío ni ser diferente de 'H' o 'M' (fila ${rowNumber})`,
      );
    }

    // Validación ADDRESS
    const address = newInsured[hiringInsuredFields.address];
    const number = newInsured[hiringInsuredFields.number];
    const portal = newInsured[hiringInsuredFields.portal];
    const stairs = newInsured[hiringInsuredFields.stairs];
    const floor = newInsured[hiringInsuredFields.floor];
    const door = newInsured[hiringInsuredFields.door];
    const postalCode = `${newInsured[hiringInsuredFields.postalCode]}`.trim();
    const town = newInsured[hiringInsuredFields.town];
    const province = newInsured[hiringInsuredFields.province];

    if (!address) {
      errors.push(`El domicilio del asegurado es obligatorio (fila ${rowNumber})`);
    }
    if (!number) {
      errors.push(`El número del domicilio es obligatorio (fila ${rowNumber})`);
    }
    if (!town) {
      errors.push(`La ciudad es obligatoria (fila ${rowNumber})`);
    }
    if (!province) {
      errors.push(`La provincia es obligatoria (fila ${rowNumber})`);
    }
    if (!postalCode) {
      errors.push(`El código postal es obligatorio (fila ${rowNumber})`);
    } else {
      // Añadimos el CÓDIGO POSTAL a la lista para su validación async
      postalCodeList.add(postalCode);
    }

    // Validación NACIONALIDAD (nationality/nationalityLabel)
    const nationalityLabel = newInsured[hiringInsuredFields.nationality].trim();
    let nationality = "";
    if (!nationalityLabel) {
      errors.push(`La nacionalidad es obligatoria (fila ${rowNumber})`);
    } else {
      const { nationalityValue, error } = getValidatedCountryValues(
        nationalityLabel,
        countries,
        rowNumber,
      );
      if (error) {
        errors.push(error);
      } else {
        nationality = nationalityValue.toUpperCase();
      }
    }

    // Validación TELÉFONO MÓVIL
    const prefix = newInsured[hiringInsuredFields.prefix]
      ? `${newInsured[hiringInsuredFields.prefix].replace("+", "")}`
      : "34";
    const mobile = newInsured[hiringInsuredFields.mobilePhone];
    const mobilePhone = `+${prefix} ${mobile}`.trim();
    const mobilePhoneErrors = validateMobilePhone(prefix, mobile, countries, rowNumber);
    if (mobilePhoneErrors.length > 0) {
      errors.push(...mobilePhoneErrors);
    }

    // Validación EMAIL
    const email = newInsured[hiringInsuredFields.email];
    if (!email) {
      errors.push(`El email es obligatorio (archivo fila ${rowNumber})`);
    } else {
      // Añadimos el EMAIL a la lista para su validación async
      emailList.add(email);
    }

    // Validación DOCUMENTO IDENTIDAD
    const docType =
      newInsured[hiringInsuredFields.docType] &&
      newInsured[hiringInsuredFields.docType].toUpperCase().trim();
    const docNumber = newInsured[hiringInsuredFields.docNumber];
    const passportDate = newInsured[hiringInsuredFields.passportDate];
    const documentErrors = validateDocument({
      docType,
      docNumber,
      nationality,
      birthDate,
      passportDate,
      rowNumber,
    });
    if (documentErrors.length > 0) {
      errors.push(...documentErrors);
    }
    if (docNumber && docNumbers.has(docNumber)) {
      errors.push(`El número del documento de identidad ${docNumber} está repetido`);
    } else {
      docNumbers.add(docNumber);
    }

    // Validación IBAN
    const iban = newInsured[hiringInsuredFields.copay];
    let areInsuredsCopayGrouped = true;
    if (iban) {
      areInsuredsCopayGrouped = false;
      const validIban = isValidIban(iban);
      if (!validIban) {
        errors.push(`Cuenta de copago no válida (fila ${rowNumber})`);
      }
    }

    // Validación strings/number obligatorios restantes
    const name = newInsured[hiringInsuredFields.name];
    const lastname1 = newInsured[hiringInsuredFields.lastname1];
    const lastname2 = newInsured[hiringInsuredFields.lastname2];

    if (!name) {
      errors.push(`El nombre es obligatorio (fila ${rowNumber})`);
    }
    if (!lastname1) {
      errors.push(`El apellido 1 es obligatorio (fila ${rowNumber})`);
    }
    if (nationality === "ESPA" && !lastname2) {
      errors.push(`El apellido 2 es obligatorio para nacionalidad España (fila ${rowNumber})`);
    }

    // Validación COVERAGES
    const GS30Value = newInsured[hiringInsuredFields.GS30] === "S";
    const GS09Value = `${newInsured[hiringInsuredFields.GS09]}`.trim();
    const GS99Value = `${newInsured[hiringInsuredFields.GS99]}`.trim();

    const isValidGS09Value = GS09ValidValues.includes(GS09Value);
    const isValidGS99Value = GS99ValidValues.includes(GS99Value);

    if (GS09Value && !isValidGS09Value) {
      errors.push(`Valor ${GS09Value} inválido para la cobertura GS09 (fila ${rowNumber})`);
    }
    if (GS99Value && !isValidGS99Value) {
      errors.push(`Valor ${GS99Value} inválido para la cobertura GS99 (fila ${rowNumber})`);
    }

    const GS30 = {
      ...coveragesGS30[0],
      checked: GS30Value,
    };
    const GS09 = {
      ...coveragesGS09[0],
      checked: !!GS09Value,
      capitalValue: GS09Value,
    };
    const GS99 = {
      ...coveragesGS99[0],
      checked: !!GS99Value,
      capitalValue: GS99Value,
    };

    const ageRangeErrors = [
      ...(GS09Value ? validateCoverageAgeRange(birthDate, GS09, rowNumber) : []),
      ...(GS99Value ? validateCoverageAgeRange(birthDate, GS99, rowNumber) : []),
    ];
    if (ageRangeErrors.length > 0) {
      errors.push(...ageRangeErrors);
    }

    // Validación representante legal
    const hasLegalRepre = newInsured[hiringInsuredFields.hasLegalRepre] === "S";
    let legalGuardian;

    const insuredAge = getInsuredAge(birthDate);
    if (!hasLegalRepre && insuredAge < 18) {
      errors.push(
        `El asegurado fila ${rowNumber} es menor de edad, debe tener un representante legal`,
      );
    }

    if (hasLegalRepre) {
      // Validación ADDRESS representante legal
      const repreAddress = newInsured[hiringInsuredFields.legalGuardian.address];
      const repreNumber = newInsured[hiringInsuredFields.legalGuardian.number];
      const reprePortal = newInsured[hiringInsuredFields.legalGuardian.portal];
      const repreStairs = newInsured[hiringInsuredFields.legalGuardian.stairs];
      const repreFloor = newInsured[hiringInsuredFields.legalGuardian.floor];
      const repreDoor = newInsured[hiringInsuredFields.legalGuardian.door];
      const reprePostalCode = `${newInsured[hiringInsuredFields.legalGuardian.postalCode]}`;
      const repreTown = newInsured[hiringInsuredFields.legalGuardian.town];
      const repreProvince = newInsured[hiringInsuredFields.legalGuardian.province];

      if (!repreAddress) {
        errors.push(`El domicilio del representante es obligatorio (fila ${rowNumber})`);
      }
      if (!repreNumber) {
        errors.push(`El número del domicilio del representante es obligatorio (fila ${rowNumber})`);
      }
      if (!repreTown) {
        errors.push(`La ciudad del representante es obligatoria (fila ${rowNumber})`);
      }
      if (!repreProvince) {
        errors.push(`La provincia del representante es obligatoria (fila ${rowNumber})`);
      }
      if (!reprePostalCode) {
        errors.push(`El código postal del representante es obligatorio (fila ${rowNumber})`);
      } else {
        // Añadimos el CÓDIGO POSTAL a la lista para su validación async
        postalCodeList.add(reprePostalCode);
      }

      // Validación TELÉFONO MÓVIL representante legal
      const reprePrefix = newInsured[hiringInsuredFields.legalGuardian.prefix]
        ? `${newInsured[hiringInsuredFields.legalGuardian.prefix].replace("+", "")}`
        : "34";
      const repreMobile = newInsured[hiringInsuredFields.legalGuardian.mobilePhone];
      const repreMobilePhone = `+${reprePrefix} ${repreMobile}`.trim();
      const repreMobilePhoneErrors = validateMobilePhone(
        reprePrefix,
        repreMobile,
        countries,
        rowNumber,
      );
      if (repreMobilePhoneErrors.length > 0) {
        errors.push(...repreMobilePhoneErrors);
      }

      // Validación EMAIL representante legal
      const repreEmail = newInsured[hiringInsuredFields.legalGuardian.email];
      if (!repreEmail) {
        errors.push(`El email del representante legal es obligatorio (fila ${rowNumber})`);
      } else {
        // Añadimos el EMAIL a la lista para su validación async
        emailList.add(repreEmail);
      }

      // Validación FECHA NACIMIENTO representante legal
      const repreBirthDate = newInsured[hiringInsuredFields.legalGuardian.birthDate];
      if (!repreBirthDate) {
        errors.push(`La fecha de nacimiento es obligatoria (fila ${rowNumber})`);
      } else if (!isValidDate(repreBirthDate)) {
        errors.push(`Fecha de nacimiento inválida: ${repreBirthDate} (fila ${rowNumber})`);
      }

      // Validación NACIONALIDAD representante legal
      const repreNationalityLabel = newInsured[hiringInsuredFields.legalGuardian.nationality];
      let repreNationality = "";
      if (!repreNationalityLabel) {
        errors.push(`La nacionalidad del representante legal es obligatoria (fila ${rowNumber})`);
      } else {
        const { nationalityValue: repreNationalityValue, error } = getValidatedCountryValues(
          repreNationalityLabel,
          countries,
          rowNumber,
        );
        if (error) {
          errors.push(error);
        } else {
          repreNationality = repreNationalityValue.toUpperCase();
        }
      }

      // Validación DOCUMENTO IDENTIDAD del representante legal
      const repreDocType =
        newInsured[hiringInsuredFields.legalGuardian.docType] &&
        newInsured[hiringInsuredFields.legalGuardian.docType].toUpperCase().trim();
      const repreDocNumber = newInsured[hiringInsuredFields.legalGuardian.docNumber];
      const reprePassportDate = newInsured[hiringInsuredFields.legalGuardian.passportDate];
      const repredocumentErrors = validateDocument({
        docType: repreDocType,
        docNumber: repreDocNumber,
        nationality: repreNationality,
        birthDate: repreBirthDate,
        passportDate: reprePassportDate,
        rowNumber,
      });
      if (repredocumentErrors.length > 0) {
        errors.push(...repredocumentErrors);
      }

      // Validación RELACIÓN CON EL ASEGURADO del representante legal
      const relationshipLabel = newInsured[hiringInsuredFields.legalGuardian.relationship].trim();
      let relationship = "";
      if (!relationshipLabel) {
        errors.push(
          `El tipo de relación del representante legal es obligatorio (fila ${rowNumber})`,
        );
      } else {
        const { relationshipValue, error } = getValidatedRelationship(
          relationshipLabel,
          relationshipList,
          rowNumber,
        );
        if (error) {
          errors.push(error);
        } else {
          relationship = relationshipValue;
        }
      }

      // Validación strings/number obligatorios restantes
      const repreName = newInsured[hiringInsuredFields.legalGuardian.name];
      const repreLastname1 = newInsured[hiringInsuredFields.legalGuardian.lastname1];
      const repreLastname2 = newInsured[hiringInsuredFields.legalGuardian.lastname2];

      if (!repreName) {
        errors.push(`El nombre del representante legal es obligatorio (fila ${rowNumber})`);
      }
      if (!repreLastname1) {
        errors.push(`El apellido 1 del representante legal es obligatorio (fila ${rowNumber}`);
      }

      legalGuardian = {
        name: repreName.toUpperCase(),
        lastname1: repreLastname1.toUpperCase(),
        lastname2: repreLastname2.toUpperCase(),
        birthDate: repreBirthDate,
        nationality: repreNationality.toUpperCase(),
        docType: repreDocType.toUpperCase(),
        docNumber: repreDocNumber,
        passportDate: reprePassportDate,
        relationship: relationship.toUpperCase(),
        mobilePhone: repreMobilePhone,
        email: repreEmail.toUpperCase(),
        address: {
          address: {
            value: repreAddress.toUpperCase(),
            code: repreAddress.toUpperCase(),
            label: repreAddress.toUpperCase(),
          },
          wayType: "calle",
          number: `${repreNumber}`,
          portal: reprePortal,
          stairs: repreStairs,
          floor: repreFloor,
          door: repreDoor,
          postalCode: reprePostalCode,
          town: {
            value: repreTown.toUpperCase(),
            code: repreTown.toUpperCase(),
            label: "",
          },
          province: {
            value: repreProvince.toUpperCase(),
            code: reprePostalCode.slice(0, 2),
            label: "",
          },
          normalizaDireciones: "N",
          sameAsTaker: false,
        },
      } as unknown as InsuredLegalRepre;
    }

    // Comprobar si el documento del Tomador está en el archivo
    let sameAsTaker = false;
    if (index === 0 && docNumber === takerDocNumber) {
      sameAsTaker = true;
      warnings.push(
        `El primer asegurado tiene el mismo número de documento que el Tomador, quedará marcada la casilla "El tomador de la póliza es el asegurado".`,
      );
    } else if (docNumber === takerDocNumber) {
      errors.push(
        `El asegurado de la fila ${rowNumber} tiene el mismo número de documento que el Tomador. Si desea que el Tomador sea asegurado coloque la información en la fila 2.`,
      );
    }

    // Nuevo INSURED
    const newInsuredData: Partial<InsuredData> = {
      sameAsTaker,
      name: name.toUpperCase(),
      lastname1: lastname1.toUpperCase(),
      lastname2: lastname2.toUpperCase(),
      birthDate,
      docType,
      docNumber: docType === "MENOR" ? "1" : docNumber,
      passportDate,
      gender,
      nationality,
      nationalityLabel: nationalityLabel.toUpperCase(),
      mobilePhone,
      email: email.toUpperCase(),
      address: {
        address: {
          value: address.toUpperCase(),
          code: address.toUpperCase(),
          label: address.toUpperCase(),
        },
        wayType: "calle",
        number: `${number}`,
        portal,
        stairs,
        floor,
        door,
        postalCode,
        town: {
          value: town.toUpperCase(),
          code: town.toUpperCase(),
          label: "",
        },
        province: {
          value: province.toUpperCase(),
          code: postalCode.slice(0, 2),
          label: "",
        },
        normalizaDireciones: "N",
        sameAsTaker,
      },
      coverages: [...mandatoryCoverages, GS30, GS09, GS99],
      areInsuredsCopayGrouped,
      // Representante legal
      hasLegalRepre,
      ...(hasLegalRepre ? { legalGuardian } : {}),
      // Valores fijos para todos los asegurados
      insured: true,
      physicalPerson: true,
      landlinePhone: "",
      beneficiaries: [],
      isDataValid: true,
      beneficiariesType: "legalHeirs",
      profession: {
        value: "",
        code: "",
        other: "",
      },
      ...(iban
        ? {
            copay: {
              iban,
              sameAsMain: false,
              sepaSignedDate: "",
            },
          }
        : {}),
    };
    newInsureds.push(newInsuredData);
    birthDates.push(birthDate);
  }

  // Validación lista códigos postales únicos para reducir llamadas
  const responsePostalCode = await Promise.all(
    Array.from(postalCodeList).map((item) => validatePostalCode(item)),
  );
  const postalCodeErrors = responsePostalCode.flat();
  if (postalCodeErrors) {
    errors.push(...postalCodeErrors);
  }

  // Validación lista emails
  const responseEmails = await Promise.all(
    Array.from(emailList).map((item) => validateEmail(item)),
  );
  const emailErrors = responseEmails.flat();
  if (emailErrors) {
    errors.push(...emailErrors);
  }

  if (errors.length) {
    return { errors, newInsureds: [] };
  }

  return { errors, newInsureds, warnings };
};
